import React from 'react';
import { Box, Rating } from '@mui/material';
import { Visibility as ViewIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';

export interface ContentStatsProps {
  content: {
    rating?: number;
    averageRating?: number;
    avg_rating?: number;
    viewCount?: number;
    view_count?: number;
    downloadCount?: number;
    download_count?: number;
    fileSize?: number | string;
    file_size?: number | string;
  };
  show?: {
    rating?: boolean;     // default true
    views?: boolean;      // default true
    downloads?: boolean;  // default true
    size?: boolean;       // default true
  };
  /** Layout direction. 'row' (default) for stats footer; 'column' for list-view side metadata. */
  direction?: 'row' | 'column';
  sx?: SxProps<Theme>;
}

const formatFileSize = (raw?: number | string): string => {
  let bytes: number;
  if (typeof raw === 'string') {
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return raw;
    bytes = parsed;
  } else if (typeof raw === 'number') {
    bytes = raw;
  } else {
    return '—';
  }
  if (bytes === 0) return '0 B';
  const units = ['B', 'K', 'M', 'G'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)}${units[i]}`;
};

/**
 * The "stats row" — rating + view count + download count + file size.
 * Pure visual; no auth.
 */
export const ContentStats: React.FC<ContentStatsProps> = ({
  content,
  show = {},
  direction = 'row',
  sx,
}) => {
  const showRating = show.rating !== false;
  const showViews = show.views !== false;
  const showDownloads = show.downloads !== false;
  const showSize = show.size !== false;
  const isColumn = direction === 'column';
  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: isColumn ? 'flex-end' : 'center',
          flexDirection: isColumn ? 'column' : 'row',
          justifyContent: isColumn ? 'flex-start' : 'space-between',
          gap: isColumn ? 0.5 : 0,
          fontSize: 12,
          color: (theme) => theme.palette.text.secondary,
          fontVariantNumeric: 'tabular-nums',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {showRating ? (
        <Rating
          value={Number(content.rating ?? content.averageRating ?? content.avg_rating ?? 0)}
          precision={0.5}
          readOnly
          size="small"
          sx={{ fontSize: '12px' }}
        />
      ) : (
        !isColumn && <span />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {showViews && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ViewIcon sx={{ fontSize: 12, opacity: 0.7 }} />
            {content.viewCount ?? content.view_count ?? 0}
          </Box>
        )}
        {showDownloads && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DownloadIcon sx={{ fontSize: 12, opacity: 0.7 }} />
            {content.downloadCount ?? content.download_count ?? 0}
          </Box>
        )}
        {!isColumn && showSize && <span>{formatFileSize(content.fileSize ?? content.file_size)}</span>}
      </Box>
      {isColumn && showSize && <span>{formatFileSize(content.fileSize ?? content.file_size)}</span>}
    </Box>
  );
};

export default ContentStats;
